/**
 * OTP 폴링 로직 공통 composable
 * CJ 택배사 SMS 인증 자동 폴링 (3초 간격, 3분 타임아웃)
 *
 * TODO: 공유 코드 추출 필요 (우선순위: HIGH)
 * - 이 파일은 Admin과 Mobile에서 100% 동일합니다 (184줄)
 * - farmfree/packages/farmfree-common/shared-composables/courier/shared-useCourierOtpPolling.ts 로 이동 권장
 *
 * TODO: Singleton 패턴 명시화 (우선순위: MEDIUM)
 * - 현재는 각 호출마다 독립 인스턴스지만, 재인증 모달이 Singleton이므로 폴링도 Singleton으로 개선 권장
 * - 중복 폴링 방지 로직 추가 필요
 */

import { ref } from 'vue'
import { createCourierSaasFunctionsService } from '@/services/courierSaas/courierSaasFunctionsService'
import type { CourierProviderId } from '@farmfree/common/constants/courier/courierProviders'

/**
 * OTP 폴링 옵션
 */
export interface OtpPollingOptions {
  companyId: string
  courierId: string
  providerId: CourierProviderId
  sessionId: string
  onSuccess: () => void
  onError: (message: string) => void
  pollingInterval?: number // 기본값: 3000ms (3초)
  pollingTimeout?: number // 기본값: 180000ms (3분)
}

/**
 * OTP 응답 타입 가드
 */
export function isOtpResponse(data: any): data is {
  requiresOtp: boolean
  sessionId?: string
  message?: string
} {
  return data && typeof data === 'object' && 'requiresOtp' in data
}

/**
 * OTP 폴링 composable
 */
export function useCourierOtpPolling() {
  const courierSaasFunctionsService = createCourierSaasFunctionsService()

  // OTP 상태
  const otpRequired = ref<boolean>(false)
  const otpSessionId = ref<string>('')
  const otpMessage = ref<string>('')
  const isPolling = ref<boolean>(false)

  // 폴링 타이머
  let pollingInterval: NodeJS.Timeout | null = null
  let pollingTimeout: NodeJS.Timeout | null = null
  let pollingCount: number = 0
  let currentOptions: OtpPollingOptions | null = null

  /**
   * OTP 폴링 시작
   */
  function startOtpPolling(options: OtpPollingOptions) {
    const interval: number = options.pollingInterval || 3000 // 기본 3초
    const timeout: number = options.pollingTimeout || 3 * 60 * 1000 // 기본 3분

    console.log(`🔄 [OTP 폴링] 시작 (${interval / 1000}초 간격, 최대 ${timeout / 60000}분)`)

    // 기존 폴링 정리
    stopOtpPolling()

    // 옵션 저장
    currentOptions = options
    otpSessionId.value = options.sessionId
    isPolling.value = true
    pollingCount = 0

    // 즉시 첫 번째 검증
    pollingCount++
    handleVerifyOtp()

    // 주기적 검증
    pollingInterval = setInterval(() => {
      pollingCount++
      handleVerifyOtp()
    }, interval)

    // 타임아웃 설정
    pollingTimeout = setTimeout(() => {
      stopOtpPolling()
      if (currentOptions) {
        currentOptions.onError('인증 시간이 초과되었습니다. 다시 시도해주세요.')
      }
    }, timeout)
  }

  /**
   * OTP 폴링 중지
   */
  function stopOtpPolling() {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
    if (pollingTimeout) {
      clearTimeout(pollingTimeout)
      pollingTimeout = null
    }
    if (isPolling.value) {
      console.log(`⏸️ [OTP 폴링] 중지 (총 ${pollingCount}회 시도)`)
      isPolling.value = false
    }
  }

  /**
   * OTP 검증 수행
   */
  async function handleVerifyOtp() {
    if (!currentOptions) return

    try {
      const response = await courierSaasFunctionsService.verifyOtp({
        companyId: currentOptions.companyId,
        courierId: currentOptions.courierId,
        sessionId: otpSessionId.value,
        otpCode: '', // CJ는 폴링 방식이므로 빈 문자열
        providerId: currentOptions.providerId,
      })

      // PENDING 상태 (SMS 미도착) - 계속 폴링
      if (!response.isSuccess && response.data?.status === 'PENDING') {
        console.log(`⏳ [${pollingCount}번째] SMS 미도착, 폴링 계속`)
        return
      }

      // 실패 - 폴링 중지
      if (!response.isSuccess) {
        stopOtpPolling()
        currentOptions.onError(response.error || 'OTP 인증 실패')
        return
      }

      // 성공 - 폴링 중지 및 완료
      stopOtpPolling()
      console.log(`✅ [${pollingCount}번째] OTP 인증 성공`)
      currentOptions.onSuccess()
    } catch (error: any) {
      stopOtpPolling()
      console.error('[OTP 폴링] 검증 실패:', error)
      if (currentOptions) {
        currentOptions.onError('OTP 검증 중 오류가 발생했습니다.')
      }
    }
  }

  /**
   * OTP 필요 상태 설정
   */
  function setOtpRequired(required: boolean, message?: string, sessionId?: string) {
    otpRequired.value = required
    if (message) otpMessage.value = message
    if (sessionId) otpSessionId.value = sessionId
  }

  /**
   * 상태 초기화
   */
  function reset() {
    stopOtpPolling()
    otpRequired.value = false
    otpSessionId.value = ''
    otpMessage.value = ''
    currentOptions = null
  }

  return {
    // 상태
    otpRequired,
    otpSessionId,
    otpMessage,
    isPolling,

    // 메서드
    startOtpPolling,
    stopOtpPolling,
    setOtpRequired,
    reset,
  }
}
